import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store, persistor } from '@/redux';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { isApiUnavailableError } from '@/services/api';
import { applyQueryDefaults } from '@/services/queryDefaults';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { reportClientError } from '@/services/clientError.service';
import App from './App';
import '@/styles/global.css';
import '@/styles/print.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * `0` chứ KHÔNG phải 5 phút như trước.
       *
       * `staleTime` 5 phút áp cho toàn bộ query nghĩa là dữ liệu được coi là
       * còn tươi suốt 5 phút, nên remount KHÔNG gọi lại API. Chuyển qua lại
       * giữa hai menu chỉ đọc lại cache cũ, và vì hai cửa làm mới còn lại
       * (`refetchOnWindowFocus`, `refetchOnReconnect`) cũng tắt nên cách duy
       * nhất lấy dữ liệu mới là F5 để xoá cache in-memory. Đó chính là lỗi
       * "phải refresh trình duyệt mới thấy số mới" ở các màn masterdata AMIS.
       *
       * Lệch về phía quá tươi chỉ tốn băng thông; lệch về phía quá cũ là hiển
       * thị sai số liệu tiền bạc. Hai loại sai này không cùng giá trong ERP.
       *
       * Nhóm nào thật sự cần giữ cache thì khai báo tường minh trong
       * `applyQueryDefaults` bên dưới.
       */
      staleTime: 0,
      retry: (failureCount, error) =>
        !isApiUnavailableError(error) && failureCount < 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      /**
       * `always` chứ KHÔNG để mặc định `'online'`.
       *
       * Mặc định của TanStack Query là TẠM DỪNG mutation khi `onlineManager` báo
       * offline. Mutation bị dừng không gửi request VÀ CŨNG KHÔNG reject —
       * `mutateAsync` treo vĩnh viễn. Chỗ gọi nào cũng chỉ có `try/catch` nên
       * không có gì chạy: không request, không thông báo, nút vẫn quay. Đó chính
       * là lỗi "tải tài liệu nhân viên không ăn thua" — người dùng bấm Lưu rồi
       * ngồi nhìn, backend không hề nhận được gì.
       *
       * Ứng dụng KHÔNG có hàng đợi offline: mutation bị dừng sẽ không bao giờ
       * được gửi lại có kiểm soát. Vậy nên thà chạy rồi hỏng ngay để người dùng
       * biết mà thử lại, còn hơn treo im lặng.
       */
      networkMode: 'always',
    },
  },
});

applyQueryDefaults(queryClient);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <BrowserRouter>
              {/* TCR-1191 — tầng NGOÀI CÙNG của chính component App: bắt lỗi
                  xảy ra TRONG THÂN HÀM App() (vd. hook bootstrap
                  `useAuthBootstrap` throw trước `return`) — những lỗi này xảy
                  ra ở tổ tiên của boundary `fullScreen` đã có sẵn BÊN TRONG
                  App.tsx (chỉ bọc `<Routes>`), nên boundary đó không bắt
                  được, lỗi lọt lên đây. Phải đặt trong <BrowserRouter> vì RouteErrorBoundary
                  cần `useLocation` (router context) và trong <Provider> vì
                  cần `useSelector` (redux context). */}
              {/* TCR-1192 — không đọc redux ở đây: tầng này bắt lỗi bootstrap
                  (có thể xảy ra TRƯỚC khi auth state đáng tin cậy), nên báo
                  lỗi không kèm tenant_id/user_id — vẫn còn route + message +
                  stack là đủ giá trị debug cho case này. */}
              <RouteErrorBoundary fullScreen onError={(error, info) => reportClientError(error, info)}>
                <App />
              </RouteErrorBoundary>
            </BrowserRouter>
          </ThemeProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
